import { useState } from "react";
import { useParams } from "react-router-dom";
import { ChatPanelLoading, DockedChatIsland } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { ChatIsland } from "@/zapi-frontend/widgets/ChatIsland";
import { LeoChat } from "@/zapi-frontend/widgets/LeoChat";
import { ChatEngineSwitch, type TChatEngine } from "@/zapi-frontend/shared/ui/chat";
import { TGenerationProgressProps } from "@/zapi-frontend/widgets/ChatIsland/ui/ChatMessageList";
import { TChapter } from "@/zapi-frontend/shared/types/projects";
import {
  countVisibleAssistantMessages,
  useChatUnreadIndicator
} from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useLeoChatStore } from "@/zapi-frontend/stores/leoChatStore";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useRqaSimConfigureStore } from "@/zapi-frontend/stores/rqaSimConfigure";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { useWorkspaceLayout } from "@/zapi-frontend/shared/hooks/useWorkspaceLayout";

type TWorkspaceRoomsIslandProps = {
  chapter?: TChapter;
  askOnly?: boolean;
  generationProgress?: TGenerationProgressProps;
};

export function WorkspaceRoomsIsland({ chapter, askOnly, generationProgress }: TWorkspaceRoomsIslandProps = {}) {
  const { id: projectId = "" } = useParams();
  const [engine, setEngine] = useState<TChatEngine>("ai");
  const activeChapter = useChapterStore((s) => s.activeChapter);
  const viewMode = useChapterStore((s) => s.viewMode);
  const chapterId = (chapter ?? activeChapter)?.id.toString() ?? "";
  const chatAssistantCount = useChatStore((s) =>
    countVisibleAssistantMessages(s.chatHistory[`${projectId}:${chapterId}`] ?? [])
  );
  const leoAssistantCount = useLeoChatStore((s) => countVisibleAssistantMessages(s.historyByProject[projectId] ?? []));

  const { selectedProject } = useProjectStore();
  const openSidebar = useProjectSidebarStore((s) => s.openSidebar);
  // The RQASim configure panel's Reset/Done footer sits where the launcher does.
  const isRqaSimConfigureOpen = useRqaSimConfigureStore((s) => s.isOpen);
  const { isChatDocked } = useWorkspaceLayout();
  const isClaudeGeneration = useFeatureFlag("claude-code-generation", selectedProject?.organizationId);
  // Template-less projects (claude-code generation) have no snippets pipeline behind
  // the legacy engine, so Leo is their only chat.
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";

  // On a runner project the Home page travels to Leo as work/home.json, so it's as editable as
  // any chapter. On a snippets project it's still built by the platform from the Executive
  // Summary, so Leo only answers there. The dashboard is never editable, either way.
  const leoCanEdit = isTemplateLess
    ? viewMode !== "dashboard"
    : viewMode !== "projectOverview" && viewMode !== "dashboard";

  // The engine switch lives only in the full chapter chat; askOnly instances stay on the legacy
  // engine unless the project has no template, where Leo is the only chat.
  const showEngineSwitch = !askOnly && isClaudeGeneration === true && !isTemplateLess;
  const engineSwitch = showEngineSwitch ? <ChatEngineSwitch value={engine} onChange={setEngine} /> : undefined;
  const isLeoActive = (isClaudeGeneration === true && isTemplateLess) || (showEngineSwitch && engine === "leo");
  const isChatKnown = String(selectedProject?.id) === projectId && isClaudeGeneration !== undefined;
  const { hasUnread, markSeen } = useChatUnreadIndicator(isLeoActive ? leoAssistantCount : chatAssistantCount, {
    ready: chapterId !== "",
    firstVisitKey: projectId ? `project:${projectId}` : undefined,
    conversationKey: `${isLeoActive ? "leo" : "chat"}:${chapterId}`
  });

  return (
    <DockedChatIsland
      appearance="design-system"
      testId="workspace-rooms-root"
      hasUnread={hasUnread}
      hideFab={!!openSidebar || isRqaSimConfigureOpen}
      onOpenChange={markSeen}
      docked={isChatDocked}
      floatingClassName="fixed right-2 top-[calc(var(--app-header-height)+0.5rem)] bottom-2"
    >
      <div data-testid="workspace-rooms-content" className="h-full">
        {!isChatKnown ? (
          <ChatPanelLoading leo={String(selectedProject?.id) === projectId && isTemplateLess} />
        ) : isLeoActive ? (
          <LeoChat
            projectId={projectId}
            headerRight={engineSwitch}
            canEdit={leoCanEdit}
            generationProgress={generationProgress}
          />
        ) : (
          <ChatIsland
            chapter={chapter}
            askOnly={askOnly}
            generationProgress={generationProgress}
            headerRight={engineSwitch}
          />
        )}
      </div>
    </DockedChatIsland>
  );
}
